
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Users, UserPlus, Activity, AlertTriangle, Building2, FileSpreadsheet, Briefcase } from 'lucide-react';

interface BetaStatsOverviewProps {
  userStats?: {
    total_users: number;
    active_today: number;
    active_this_week: number;
    new_signups_today: number;
    new_signups_this_week: number;
    total_candidates: number;
    total_clients: number;
    active_organizations: number;
  };
  errorSummary?: {
    total_errors_today: number;
    total_errors_this_week: number;
    unresolved_errors: number;
    top_error_type: string | null;
    error_count: number;
  };
}

export const BetaStatsOverview: React.FC<BetaStatsOverviewProps> = ({
  userStats,
  errorSummary,
}) => {
  const stats = [
    {
      title: 'Total Users',
      value: userStats?.total_users?.toLocaleString() || '0',
      description: 'Registered beta users',
      icon: Users,
      color: 'text-blue-600',
    },
    {
      title: 'Active Today',
      value: userStats?.active_today?.toLocaleString() || '0',
      description: 'Users active in last 24h',
      icon: Activity,
      color: 'text-green-600',
    },
    {
      title: 'New Signups',
      value: userStats?.new_signups_today?.toLocaleString() || '0',
      description: 'New registrations today',
      icon: UserPlus,
      color: 'text-purple-600',
    },
    {
      title: 'Total Candidates',
      value: userStats?.total_candidates?.toLocaleString() || '0',
      description: 'Candidates across all organizations',
      icon: FileSpreadsheet,
      color: 'text-orange-600',
    },
    {
      title: 'Total Clients',
      value: userStats?.total_clients?.toLocaleString() || '0',
      description: 'Clients across all organizations',
      icon: Briefcase,
      color: 'text-cyan-600',
    },
    {
      title: 'Active Organizations',
      value: userStats?.active_organizations?.toLocaleString() || '0',
      description: 'Organizations with users',
      icon: Building2,
      color: 'text-indigo-600',
    },
    {
      title: 'System Errors',
      value: errorSummary?.unresolved_errors?.toLocaleString() || '0',
      description: 'Unresolved issues',
      icon: AlertTriangle,
      color: 'text-red-600',
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {stats.map((stat) => (
        <Card key={stat.title}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{stat.title}</CardTitle>
            <stat.icon className={`h-4 w-4 ${stat.color}`} />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stat.value}</div>
            <p className="text-xs text-muted-foreground">{stat.description}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
